import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Database, Download, Trash2, Send, RefreshCw, Save, AlertTriangle, CheckCircle2, Clock } from "lucide-react";
import {
  listarBackups,
  criarBackupManual,
  apagarBackup,
  reenviarBackupWebhook,
  definirWebhookUrl,
  definirAgendaBackup,
  baixarBackup,
} from "../services/backups";
import { useConfirm } from "../components/ConfirmProvider";

export function Backups() {
  const qc = useQueryClient();
  const confirm = useConfirm();

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["backups"],
    queryFn: listarBackups,
    refetchInterval: 30000,
  });

  const [webhookUrl, setWebhookUrl] = useState("");
  const [webhookSalvo, setWebhookSalvo] = useState(false);

  // Agenda (a cada X dias, às HH:MM)
  const [intervaloDias, setIntervaloDias] = useState(1);
  const [horario, setHorario] = useState("03:00");
  const [agendaSalva, setAgendaSalva] = useState(false);

  // Hidrata input com valor salvo na primeira carga
  useEffect(() => {
    if (data?.webhookUrl !== undefined) {
      setWebhookUrl(data.webhookUrl);
    }
  }, [data?.webhookUrl]);

  useEffect(() => {
    if (data?.agenda) {
      setIntervaloDias(data.agenda.intervaloDias);
      setHorario(`${String(data.agenda.hora).padStart(2, "0")}:${String(data.agenda.minuto).padStart(2, "0")}`);
    }
  }, [data?.agenda?.intervaloDias, data?.agenda?.hora, data?.agenda?.minuto]);

  const criarM = useMutation({
    mutationFn: criarBackupManual,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backups"] }),
  });
  const apagarM = useMutation({
    mutationFn: apagarBackup,
    onSuccess: () => qc.invalidateQueries({ queryKey: ["backups"] }),
  });
  const reenviarM = useMutation({
    mutationFn: reenviarBackupWebhook,
  });
  const salvarUrlM = useMutation({
    mutationFn: () => definirWebhookUrl(webhookUrl),
    onSuccess: () => {
      setWebhookSalvo(true);
      setTimeout(() => setWebhookSalvo(false), 2500);
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
  });
  const salvarAgendaM = useMutation({
    mutationFn: () => {
      const [h, m] = horario.split(":").map((n) => parseInt(n) || 0);
      return definirAgendaBackup({ intervaloDias, hora: h, minuto: m });
    },
    onSuccess: () => {
      setAgendaSalva(true);
      setTimeout(() => setAgendaSalva(false), 2500);
      qc.invalidateQueries({ queryKey: ["backups"] });
    },
  });

  async function aoApagar(nome: string) {
    const ok = await confirm({
      title: `Apagar backup "${nome}"?`,
      message: "O arquivo será removido do servidor em definitivo. Backups enviados a destinos externos não são afetados.",
      danger: true,
      confirmText: "Apagar",
    });
    if (ok) apagarM.mutate(nome);
  }

  function fmtTam(bytes: number) {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-800 dark:text-slate-100 flex items-center gap-2">
          <Database size={20} /> Backups do banco
        </h1>
        <p className="text-sm text-slate-500 dark:text-slate-400 mt-0.5">
          Backups são gerados automaticamente conforme a rotina abaixo (local <code>backend/backups/</code>).
          Quando configurado, cada backup também é enviado pra um webhook externo.
        </p>
      </div>

      {/* Agenda automática */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-3">
        <div>
          <h2 className="font-semibold text-slate-800 dark:text-slate-100 inline-flex items-center gap-2">
            <Clock size={16} /> Rotina de backup automático
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Define a cada quantos dias o backup é gerado e em qual horário do dia. Hora local do servidor.
          </p>
        </div>
        <div className="flex items-end gap-3 flex-wrap">
          <div>
            <label className="block text-xs text-slate-500 mb-1">A cada</label>
            <div className="inline-flex items-center gap-1.5">
              <input
                type="number"
                min={1}
                max={30}
                value={intervaloDias}
                onChange={(e) => setIntervaloDias(Math.max(1, Math.min(30, parseInt(e.target.value) || 1)))}
                className="w-20 px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
              />
              <span className="text-sm text-slate-600 dark:text-slate-300">dia(s)</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-slate-500 mb-1">Às</label>
            <input
              type="time"
              value={horario}
              onChange={(e) => setHorario(e.target.value)}
              className="px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
            />
          </div>
          <button
            onClick={() => salvarAgendaM.mutate()}
            disabled={salvarAgendaM.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Save size={14} /> {salvarAgendaM.isPending ? "Salvando…" : "Salvar agenda"}
          </button>
          {agendaSalva && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={14} /> Agenda salva
            </span>
          )}
        </div>
        <div className="text-xs text-slate-500 dark:text-slate-400 grid grid-cols-1 sm:grid-cols-2 gap-1">
          <div>
            <strong>Próxima execução:</strong>{" "}
            {data?.proximaExecucao ? new Date(data.proximaExecucao).toLocaleString("pt-BR") : "—"}
          </div>
          <div>
            <strong>Última execução:</strong>{" "}
            {data?.ultimaExecucao ? new Date(data.ultimaExecucao).toLocaleString("pt-BR") : "nunca"}
          </div>
        </div>
      </div>

      {/* Webhook config */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h2 className="font-semibold text-slate-800 dark:text-slate-100">Webhook externo</h2>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              URL que receberá <code>POST</code> com o binário do <code>.db</code> (Content-Type:
              {" "}<code>application/octet-stream</code>). Headers extras: <code>X-Backup-Filename</code>, <code>X-Backup-Size</code>.
              Envio é fire-and-forget (não bloqueia o backup local).
            </p>
          </div>
          {data?.webhookEnv && (
            <span className="text-[10px] uppercase font-bold text-amber-700 bg-amber-100 px-2 py-1 rounded">
              env BACKUP_WEBHOOK_URL ativo
            </span>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <input
            type="url"
            value={webhookUrl}
            onChange={(e) => setWebhookUrl(e.target.value)}
            placeholder="https://seu-servico.com/backup-recebe"
            className="flex-1 min-w-[260px] px-3 py-2 rounded-lg border border-slate-300 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 text-sm outline-none focus:border-blue-500"
          />
          <button
            onClick={() => salvarUrlM.mutate()}
            disabled={salvarUrlM.isPending}
            className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            <Save size={14} /> {salvarUrlM.isPending ? "Salvando…" : "Salvar"}
          </button>
          {webhookSalvo && (
            <span className="text-xs text-emerald-700 inline-flex items-center gap-1">
              <CheckCircle2 size={14} /> URL salva
            </span>
          )}
        </div>
        <p className="text-[11px] text-slate-500 dark:text-slate-400">
          Vazio = só backup local. A env <code>BACKUP_WEBHOOK_URL</code> sobrescreve essa config se estiver definida.
        </p>
      </div>

      {/* Ações */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => criarM.mutate()}
          disabled={criarM.isPending}
          className="bg-emerald-600 hover:bg-emerald-700 text-white px-4 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-50"
        >
          <Database size={14} /> {criarM.isPending ? "Criando…" : "Criar backup agora"}
        </button>
        <button
          onClick={() => refetch()}
          className="bg-white hover:bg-slate-50 text-slate-700 border border-slate-200 px-3 py-2 rounded-lg text-sm font-medium inline-flex items-center gap-1.5"
        >
          <RefreshCw size={13} /> Atualizar lista
        </button>
      </div>

      {/* Lista */}
      <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 dark:bg-slate-800 text-slate-500 uppercase text-xs">
            <tr>
              <th className="text-left px-4 py-2 font-medium">Arquivo</th>
              <th className="text-left px-4 py-2 font-medium">Origem</th>
              <th className="text-right px-4 py-2 font-medium">Tamanho</th>
              <th className="text-left px-4 py-2 font-medium">Criado em</th>
              <th className="text-right px-4 py-2 font-medium"></th>
            </tr>
          </thead>
          <tbody className="text-slate-700 dark:text-slate-200">
            {isLoading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400">Carregando…</td></tr>
            )}
            {!isLoading && (data?.arquivos || []).length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-slate-400">
                  <AlertTriangle size={16} className="inline mr-1" />
                  Nenhum backup salvo ainda. Clique em "Criar backup agora".
                </td>
              </tr>
            )}
            {(data?.arquivos || []).map((f) => (
              <tr key={f.nome} className="border-t border-slate-100 dark:border-slate-800">
                <td className="px-4 py-2 font-mono text-xs">{f.nome}</td>
                <td className="px-4 py-2">
                  {f.manual ? (
                    <span className="text-[10px] uppercase font-bold text-blue-700 bg-blue-100 px-1.5 py-0.5 rounded">manual</span>
                  ) : (
                    <span className="text-[10px] uppercase font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded">automático</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right tabular-nums">{fmtTam(f.tamanhoBytes)}</td>
                <td className="px-4 py-2 text-slate-500">{new Date(f.criadoEm).toLocaleString("pt-BR")}</td>
                <td className="px-4 py-2 text-right">
                  <div className="inline-flex items-center gap-0.5">
                    <button
                      onClick={() => baixarBackup(f.nome)}
                      className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded"
                      title="Baixar arquivo"
                    >
                      <Download size={14} />
                    </button>
                    <button
                      onClick={() => reenviarM.mutate(f.nome)}
                      disabled={reenviarM.isPending || (!data?.webhookUrl && !data?.webhookEnv)}
                      className="p-1.5 text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 rounded disabled:opacity-30"
                      title={(!data?.webhookUrl && !data?.webhookEnv) ? "Configure o webhook primeiro" : "Reenviar pro webhook"}
                    >
                      <Send size={14} />
                    </button>
                    <button
                      onClick={() => aoApagar(f.nome)}
                      disabled={apagarM.isPending}
                      className="p-1.5 text-red-500 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-900/30 rounded"
                      title="Apagar"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="text-[11px] text-slate-500 dark:text-slate-400">
        Caminho do banco: <code>C:\IA\growgestor\backend\prisma\dev.db</code> ·
        Pasta de backups: <code>C:\IA\growgestor\backend\backups\</code>
      </p>
    </div>
  );
}
